import cn from 'classnames';

import type { IIconButtonProps } from './types';
import styles from './iconButton.module.scss';

/**
 * A button that is only an icon: a native `<button>` with no chrome of its own, so it
 * takes exactly the size of the icon inside it and fits wherever a bare icon used to —
 * table cells, tags, popover corners, date-picker headers.
 *
 * It exists because an icon never acts (see `Icon/BaseIcon`): the handler, the name and
 * the focus belong to a real button. `ariaLabel` is required because the button has no
 * visible text to be named by. The icon keeps its own class for colour and size; the
 * button inherits `color` by default, so a monochrome icon follows the surrounding text
 * unless its class says otherwise; `color="primary"` gives it the Design System link
 * colour and hover.
 *
 * For a button with a visible label, or one that needs the Design System's sizing and
 * colour variants, use `uilib/Button` with its `icon` prop instead.
 */
const IconButton = ({
  icon,
  ariaLabel,
  dataTest,
  color = 'inherit',
  type = 'button',
  className,
  ref,
  ...restProps
}: IIconButtonProps) => (
  <button
    ref={ ref }
    type={ type === 'submit' ? 'submit' : 'button' }
    className={ cn(styles.iconButton, { [styles.primary]: color === 'primary' }, className) }
    aria-label={ ariaLabel }
    data-test={ dataTest }
    { ...restProps }
  >
    { icon }
  </button>
);

export default IconButton;
